import {AfterViewInit, DestroyRef, Directive, inject} from "@angular/core";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {MatOption, MatSelect} from "@angular/material/select";

@Directive({
    selector: "mat-option[select-all]",
    standalone: true,
})
export class SelectAllDirective implements AfterViewInit {
    private readonly matSelect = inject(MatSelect);
    private readonly matOption = inject(MatOption);
    private readonly destroyRef = inject(DestroyRef);

    ngAfterViewInit(): void {
        // Toggle every other option when the "select all" option is clicked.
        this.matOption.onSelectionChange
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((ev) => {
                if (!ev.isUserInput) return;
                if (ev.source.selected) {
                    this.selectAll()
                } else {
                    this.deselectAll();
                }
            });

        // Keep the "select all" option in sync whenever another option changes:
        // unchecking any option must uncheck "select all", and checking the last
        // remaining option must check it.
        this.matSelect.optionSelectionChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((ev) => {
                if (!ev.isUserInput || ev.source === this.matOption) return;
                this.syncSelectAllState();
            });

        // Initialize the "select all" state from the current value.
        Promise.resolve().then(() => this.syncSelectAllState());
    }

    private get formControl() {
        return this.matSelect.ngControl?.control;
    }

    /** Every option in the select except the "select all" one. */
    private get otherOptions(): MatOption[] {
        return this.matSelect.options.filter((o) => o !== this.matOption);
    }

    private selectAll(): void {
        this.formControl?.setValue(this.otherOptions.map((o) => o.value));
        // Mark visually selected without emitting so its value stays out of the model.
        this.matOption.select(false);
    }

    private deselectAll(): void {
        this.formControl?.setValue([]);
        this.matOption.deselect(false);
    }

    private syncSelectAllState(): void {
        const others = this.otherOptions;
        const allSelected = others.length > 0 && others.every((o) => o.selected);

        if (allSelected) {
            this.matOption.select(false)
        } else {
            this.matOption.deselect(false);
        }
    }
}
